import { get, post } from '@/lib/api/client'
import {
  type GalleryFolder,
  type PhotoGallery,
  type PublicGalleryItem,
  type GalleryFilterParams,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams,
  type GalleryFolderSearchResult
} from '@/lib/api/types'
import { createQueryString } from '@/utils/helpers/createQueryString'

export class GalleryService {
  /**
   * Get all folders (Public endpoint)
   * GET /folders/public/all?folderType=PHOTO|VIDEO
   */
  async getFolders(folderType?: 'PHOTO' | 'VIDEO'): Promise<ApiResponse<GalleryFolder[]>> {
    const queryParams = new URLSearchParams()
    if (folderType) {
      queryParams.append('folderType', folderType)
    }
    const queryString = queryParams.toString()
    const endpoint = queryString ? `folders/public/all?${queryString}` : 'folders/public/all'

    return await get<ApiResponse<GalleryFolder[]>>(endpoint)
  }

    /**
     * Search photo folders by query (Public endpoint)
     * GET /folders/public/search?query={query}
     * Note: This endpoint is not paginated and returns all matching folders
     */
    async searchPhotoFolders(query: string, params: PaginationParams): Promise<ApiResponse<PaginatedResponse<GalleryFolderSearchResult>>> {
      const queryString = createQueryString({...params, searchBy: query, folderType: 'PHOTO' })
      return await get<ApiResponse<PaginatedResponse<GalleryFolderSearchResult>>>(
        `folders/public/search?${queryString}`
      )
    }

  /**
   * Search photo galleries with pagination (Public endpoint)
   * GET /photo-galleries/public/search?searchBy&size={size}&page={page}
   */
  async searchGalleries(
    params: PaginationParams,
    query: string
  ): Promise<ApiResponse<PaginatedResponse<PhotoGallery>>> {
    

    // Build query string matching the API format: searchBy&size=10&page=1
    const queryParams = createQueryString({ ...params, searchBy: query })

    const endpoint = `photo-galleries/public/search?${queryParams}`

    return await get<ApiResponse<PaginatedResponse<PhotoGallery>>>(
      endpoint
    )
  }

  /**
   * Filter photo galleries with pagination (Public endpoint)
   * POST /galleries/public/filter?size={size}&page={page}
   * Returns a flat list of published galleries
   */
  async filterGalleries(
    params: PaginationParams,
    filters: GalleryFilterParams
  ): Promise<ApiResponse<PaginatedResponse<PublicGalleryItem>>> {
   

    // Build query string
    const queryParams = createQueryString(params)

    const endpoint = `photo-galleries/public/filter?${queryParams}`

    // Build POST body
    const body = {...filters}
    
    return await post<ApiResponse<PaginatedResponse<PublicGalleryItem>>>(
      endpoint,
      body
    )
  }

  /**
   * Get home page highlights
   * GET /galleries/public/highlights?type=HOME_PAGE_IS_KEY_HIGHLIGHT
   */
  async getHomePageHighlights(): Promise<ApiResponse<PhotoGallery[]>> {
    return await get<ApiResponse<PhotoGallery[]>>(
      'photo-galleries/public/highlights?type=HOME_PAGE_IS_KEY_HIGHLIGHT'
    )
  }
}

// Export singleton instance
export const galleryService = new GalleryService()

// Export default
export default galleryService

